import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ZoomIn, ZoomOut, RotateCcw, BookOpen, Link2, Target } from 'lucide-react';

interface KnowledgeNode {
  id: number;
  code: string;
  name: string;
  nameCn?: string;
  description?: string;
  nodeType: 'topic' | 'concept' | 'skill';
  subjectId: number;
  gradeLevel?: number;
  difficulty: number;
  parentId?: number;
}

interface KnowledgeEdge {
  id: number;
  sourceId: number;
  targetId: number;
  relationType: 'prerequisite' | 'related' | 'extends' | 'applies';
  strength: number;
}

interface GraphNode extends KnowledgeNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number;
  fy?: number;
}

interface GraphData {
  nodes: GraphNode[];
  edges: KnowledgeEdge[];
}

export function KnowledgeGraphPage() {
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetchGraphData();
  }, []);

  async function fetchGraphData() {
    try {
      const [nodesRes, edgesRes] = await Promise.all([
        fetch('/api/vce/mathematics/knowledge-nodes'),
        fetch('/api/vce/mathematics/prerequisites'),
      ]);

      const nodesData = await nodesRes.json();
      const edgesData = await edgesRes.json();

      const nodes: GraphNode[] = (nodesData.data || nodesData || []).map((n: KnowledgeNode, i: number) => ({
        ...n,
        x: 200 + Math.cos((i * 2 * Math.PI) / 20) * 150,
        y: 200 + Math.sin((i * 2 * Math.PI) / 20) * 150,
        vx: 0,
        vy: 0,
      }));

      const edges: KnowledgeEdge[] = edgesData.data || edgesData || [];

      setGraphData({ nodes, edges });
    } catch (error) {
      console.error('Failed to fetch graph data:', error);
      setGraphData({
        nodes: generateMockNodes(),
        edges: generateMockEdges(),
      });
    } finally {
      setLoading(false);
    }
  }

  function generateMockNodes(): GraphNode[] {
    const concepts = [
      { id: 1, code: 'ALG.01', name: 'Linear Equations', nameCn: '线性方程', difficulty: 2, nodeType: 'concept' as const, subjectId: 1 },
      { id: 2, code: 'ALG.02', name: 'Quadratic Equations', nameCn: '二次方程', difficulty: 3, nodeType: 'concept' as const, subjectId: 1 },
      { id: 3, code: 'ALG.03', name: 'Polynomials', nameCn: '多项式', difficulty: 4, nodeType: 'concept' as const, subjectId: 1 },
      { id: 4, code: 'CAL.01', name: 'Differentiation', nameCn: '微分', difficulty: 4, nodeType: 'concept' as const, subjectId: 1 },
      { id: 5, code: 'CAL.02', name: 'Integration', nameCn: '积分', difficulty: 4, nodeType: 'concept' as const, subjectId: 1 },
      { id: 6, code: 'GEO.01', name: 'Triangles', nameCn: '三角形', difficulty: 2, nodeType: 'concept' as const, subjectId: 1 },
      { id: 7, code: 'GEO.02', name: 'Trigonometry', nameCn: '三角学', difficulty: 3, nodeType: 'concept' as const, subjectId: 1 },
      { id: 8, code: 'STA.01', name: 'Probability', nameCn: '概率', difficulty: 3, nodeType: 'concept' as const, subjectId: 1 },
    ];

    return concepts.map((n, i) => ({
      ...n,
      gradeLevel: 11,
      x: 300 + Math.cos((i * 2 * Math.PI) / concepts.length) * 180,
      y: 250 + Math.sin((i * 2 * Math.PI) / concepts.length) * 180,
      vx: 0,
      vy: 0,
    }));
  }

  function generateMockEdges(): KnowledgeEdge[] {
    return [
      { id: 1, sourceId: 1, targetId: 2, relationType: 'prerequisite', strength: 0.9 },
      { id: 2, sourceId: 2, targetId: 3, relationType: 'prerequisite', strength: 0.8 },
      { id: 3, sourceId: 2, targetId: 4, relationType: 'prerequisite', strength: 0.7 },
      { id: 4, sourceId: 4, targetId: 5, relationType: 'prerequisite', strength: 0.9 },
      { id: 5, sourceId: 6, targetId: 7, relationType: 'prerequisite', strength: 0.8 },
      { id: 6, sourceId: 7, targetId: 4, relationType: 'related', strength: 0.6 },
    ];
  }

  useEffect(() => {
    if (!graphData || !canvasRef.current) return;
    drawGraph();
  }, [graphData, zoom, offset, selectedNode]);

  function drawGraph() {
    const canvas = canvasRef.current;
    if (!canvas || !graphData) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);
    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(zoom, zoom);

    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;

    graphData.edges.forEach(edge => {
      const source = graphData.nodes.find(n => n.id === edge.sourceId);
      const target = graphData.nodes.find(n => n.id === edge.targetId);
      if (!source || !target) return;

      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);

      if (edge.relationType === 'prerequisite') {
        ctx.strokeStyle = '#3b82f6';
        ctx.setLineDash([]);
      } else if (edge.relationType === 'related') {
        ctx.strokeStyle = '#94a3b8';
        ctx.setLineDash([5, 5]);
      } else {
        ctx.strokeStyle = '#e2e8f0';
        ctx.setLineDash([]);
      }

      ctx.lineWidth = edge.strength * 2;
      ctx.stroke();

      const angle = Math.atan2(target.y - source.y, target.x - source.x);
      const arrowX = target.x - Math.cos(angle) * 25;
      const arrowY = target.y - Math.sin(angle) * 25;
      
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(
        arrowX - 10 * Math.cos(angle - Math.PI / 6),
        arrowY - 10 * Math.sin(angle - Math.PI / 6)
      );
      ctx.lineTo(
        arrowX - 10 * Math.cos(angle + Math.PI / 6),
        arrowY - 10 * Math.sin(angle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fillStyle = ctx.strokeStyle;
      ctx.fill();
    });

    graphData.nodes.forEach(node => {
      const isSelected = selectedNode?.id === node.id;
      const nodeRadius = 20 + node.difficulty * 3;

      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);

      if (node.nodeType === 'concept') {
        ctx.fillStyle = isSelected ? '#3b82f6' : '#dbeafe';
      } else if (node.nodeType === 'skill') {
        ctx.fillStyle = isSelected ? '#10b981' : '#d1fae5';
      } else {
        ctx.fillStyle = isSelected ? '#f59e0b' : '#fef3c7';
      }
      ctx.fill();

      ctx.strokeStyle = isSelected ? '#1d4ed8' : '#94a3b8';
      ctx.lineWidth = isSelected ? 3 : 1;
      ctx.stroke();

      ctx.fillStyle = '#1e293b';
      ctx.font = '10px system-ui';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      const label = node.name.length > 12 ? node.name.slice(0, 10) + '...' : node.name;
      ctx.fillText(label, node.x, node.y);
    });

    ctx.restore();
  }

  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas || !graphData) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - offset.x) / zoom;
    const y = (e.clientY - rect.top - offset.y) / zoom;

    const clickedNode = graphData.nodes.find(node => {
      const distance = Math.sqrt((node.x - x) ** 2 + (node.y - y) ** 2);
      return distance < 25;
    });

    setSelectedNode(clickedNode || null);
  }

  function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    setIsDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  }

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!isDragging) return;
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }

  function handleMouseUp() {
    setIsDragging(false);
  }

  function handleZoomIn() {
    setZoom(z => Math.min(z * 1.2, 3));
  }

  function handleZoomOut() {
    setZoom(z => Math.max(z / 1.2, 0.3));
  }

  function handleReset() {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setSelectedNode(null);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">知识图谱</h1>
          <p className="text-slate-600">可视化知识点之间的先修关系和关联</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleZoomIn}
            className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <ZoomIn className="w-5 h-5 text-slate-600" />
          </button>
          <button
            onClick={handleZoomOut}
            className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <ZoomOut className="w-5 h-5 text-slate-600" />
          </button>
          <button
            onClick={handleReset}
            className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <RotateCcw className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <canvas
            ref={canvasRef}
            width={800}
            height={500}
            className="w-full cursor-grab active:cursor-grabbing"
            onClick={handleCanvasClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-blue-500" />
              图例
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-100 border border-blue-300" />
                <span className="text-slate-600">概念</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100 border border-green-300" />
                <span className="text-slate-600">技能</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-100 border border-amber-300" />
                <span className="text-slate-600">主题</span>
              </div>
              <div className="border-t border-slate-100 pt-2 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-0.5 bg-blue-500" />
                  <span className="text-slate-600">先修关系</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-0.5 bg-slate-400 border-dashed border-t-2 border-slate-400" />
                  <span className="text-slate-600">相关关系</span>
                </div>
              </div>
            </div>
          </div>

          {selectedNode && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200 p-4"
            >
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-500" />
                选中节点
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-slate-500">代码</span>
                  <code className="block text-sm bg-slate-100 px-2 py-1 rounded mt-1">
                    {selectedNode.code}
                  </code>
                </div>
                <div>
                  <span className="text-xs text-slate-500">名称</span>
                  <p className="text-sm font-medium text-slate-900 mt-1">{selectedNode.name}</p>
                  {selectedNode.nameCn && (
                    <p className="text-xs text-slate-500">{selectedNode.nameCn}</p>
                  )}
                </div>
                <div className="flex gap-4">
                  <div>
                    <span className="text-xs text-slate-500">难度</span>
                    <p className="text-sm font-medium text-slate-900 mt-1">
                      {selectedNode.difficulty}/5
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">类型</span>
                    <p className="text-sm font-medium text-slate-900 mt-1 capitalize">
                      {selectedNode.nodeType}
                    </p>
                  </div>
                </div>
                {selectedNode.description && (
                  <div>
                    <span className="text-xs text-slate-500">描述</span>
                    <p className="text-xs text-slate-600 mt-1">{selectedNode.description}</p>
                  </div>
                )}
                {graphData && (
                  <div>
                    <span className="text-xs text-slate-500">关系</span>
                    <div className="flex items-center gap-1 mt-1">
                      <Link2 className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-600">
                        {graphData.edges.filter(e => 
                          e.sourceId === selectedNode.id || e.targetId === selectedNode.id
                        ).length} 个关联
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
